import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { WS_STATUS, STEP_STATUS, PORTAL_KEY } from "@/lib/track";
import { useLightbox } from "@/components/Lightbox";
import { fmtDate, fmtDateTime } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import * as Icons from "lucide-react";

const POLL_MS = 30000;

export default function ClientPortal() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [state, setState] = useState("loading"); // loading | invalid | ready
  const [ws, setWs] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [openReq, setOpenReq] = useState(false);
  const [reqKind, setReqKind] = useState("prestation");
  const imgCache = useRef({});
  const lb = useLightbox();

  // Échange du jeton contre une session, puis nettoyage du secret de l'URL.
  useEffect(() => {
    let done = false;
    axios.post(`${API}/track/portal/session`, { token })
      .then((r) => { if (done) return; setSession(r.data.session); sessionStorage.setItem(PORTAL_KEY, r.data.session);
        window.history.replaceState({}, "", "/suivi"); })
      .catch(() => setState("invalid"));
    return () => { done = true; };
  }, [token]);

  const load = useCallback(async () => {
    if (!session) return;
    try { const { data } = await axios.get(`${API}/track/portal/worksite`, { headers: { Authorization: `Bearer ${session}` } }); setWs(data); setState("ready"); }
    catch { setState("invalid"); }
  }, [session]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!session) return;
    const t = setInterval(load, POLL_MS);
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { clearInterval(t); window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, [session, load]);

  // Charge une image protégée (avec header de session) en blob url.
  const [imgs, setImgs] = useState({});
  const loadImg = useCallback(async (fid) => {
    if (imgCache.current[fid]) return;
    imgCache.current[fid] = true;
    try {
      const { data } = await axios.get(`${API}/track/portal/file/${fid}`, { headers: { Authorization: `Bearer ${session}` }, responseType: "blob" });
      setImgs((p) => ({ ...p, [fid]: URL.createObjectURL(data) }));
    } catch { /* ignore */ }
  }, [session]);

  if (state === "loading") return <Centered><Icons.Loader2 className="h-6 w-6 animate-spin text-emerald-600" /><p className="mt-3 text-sm text-slate-500">Chargement de votre suivi…</p></Centered>;
  if (state === "invalid") return <Centered><Icons.LinkIcon className="h-8 w-8 text-slate-300" /><p className="mt-3 font-display text-lg font-semibold text-slate-700" data-testid="portal-invalid">Lien invalide ou expiré</p><p className="mt-1 max-w-sm text-sm text-slate-500">Ce lien de suivi n'est plus valide. Contactez SIGNA CLEAN pour recevoir un nouvel accès.</p></Centered>;

  const m = WS_STATUS[ws.status] || WS_STATUS.planifie;
  const cover = ws.cover_file_id;
  if (cover) loadImg(cover);
  ws.photos.forEach((p) => loadImg(p.id));
  ws.events.forEach((e) => e.photos.forEach((p) => loadImg(p.id)));

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-16" data-testid="portal-page">
      {/* Bandeau */}
      <div className="relative overflow-hidden bg-[#0D2E24]">
        {cover && imgs[cover] && <img src={imgs[cover]} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-40" />}
        <div className="relative z-10 mx-auto max-w-3xl px-5 py-8">
          <img src="/signa-logo.png" alt="SIGNA CLEAN" className="h-8 w-auto" />
          <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-emerald-300/90" data-testid="portal-client-name">{[ws.client_name, ws.site_name].filter(Boolean).join(" · ")}</p>
          <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">{ws.title}</h1>
          <p className="mt-1 text-sm text-white/80">Suivez l'avancement de nos interventions.</p>
        </div>
      </div>

      <div className="mx-auto max-w-3xl space-y-4 px-4 py-5">
        {!online && <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-sm text-amber-800" data-testid="portal-offline"><Icons.WifiOff className="mr-1.5 inline h-4 w-4" />Connexion perdue — les informations peuvent ne pas être à jour.</div>}

        <Card className="flex items-center justify-between p-4">
          <div><span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-sm font-medium ${m.cls}`}><span className={`h-2 w-2 rounded-full ${m.dot}`} />{m.label}</span>
            {ws.status_client_note && <p className="mt-1 text-sm text-slate-600">{ws.status_client_note}</p>}</div>
          <div className="text-right"><p className="text-xs text-slate-400">Dernière mise à jour</p><p className="text-sm font-medium text-slate-700">{ws.last_published_at ? fmtDate(ws.last_published_at) : "—"}</p>
            <button onClick={load} className="mt-1 text-xs text-emerald-700 hover:underline" data-testid="portal-refresh"><Icons.RefreshCw className="mr-1 inline h-3 w-3" />Actualiser</button></div>
        </Card>

        {/* Étapes */}
        <Card className="p-4">
          <h2 className="font-display text-base font-semibold text-[#0D2E24]">Avancement ({ws.steps_done}/{ws.steps_total} étapes terminées)</h2>
          <ul className="mt-3 space-y-2">
            {ws.steps.map((s, i) => { const sm = STEP_STATUS[s.status] || STEP_STATUS.a_faire; return (
              <li key={i} className="flex items-center gap-2 text-sm"><span className={`rounded px-2 py-0.5 text-xs font-medium ${sm.cls}`}>{sm.label}</span><span className="text-slate-700">{s.label}</span>{(s.status_at || s.done_date) && <span className="ml-auto text-xs text-slate-400">{s.status_at ? fmtDateTime(s.status_at) : fmtDate(s.done_date)}</span>}</li>
            ); })}
            {ws.steps.length === 0 && <p className="text-sm text-slate-400">Étapes à venir.</p>}
          </ul>
        </Card>

        {/* Journal */}
        {ws.events.length > 0 && (
          <Card className="p-4">
            <h2 className="font-display text-base font-semibold text-[#0D2E24]">Actualités</h2>
            <ul className="mt-3 space-y-3">
              {ws.events.map((e) => (
                <li key={e.id} className="border-l-2 border-emerald-200 pl-3">
                  <p className="text-xs text-slate-400">{fmtDate(e.event_date)}</p>
                  {e.caption && <p className="text-sm text-slate-700">{e.caption}{e.zone ? ` — ${e.zone}` : ""}</p>}
                  {e.photos.length > 0 && <div className="mt-1.5 flex flex-wrap gap-1.5">{e.photos.map((p) => imgs[p.id] && <img key={p.id} src={imgs[p.id]} alt={p.caption || ""} onClick={() => { const g = e.photos.filter((x) => imgs[x.id]).map((x) => ({ src: imgs[x.id], caption: x.caption })); lb.open(g, g.findIndex((x) => x.src === imgs[p.id])); }} className="h-20 w-20 cursor-zoom-in rounded object-cover" />)}</div>}
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Avant / Après */}
        {(() => {
          const byId = Object.fromEntries(ws.photos.map((p) => [p.id, p]));
          const pairs = ws.photos.filter((p) => p.pair_with && byId[p.pair_with]).map((p) => ({ avant: byId[p.pair_with], apres: p }));
          const pairedIds = new Set(pairs.flatMap((pr) => [pr.avant.id, pr.apres.id]));
          const singles = ws.photos.filter((p) => !pairedIds.has(p.id));
          return (
            <>
              {pairs.length > 0 && (
                <Card className="p-4" data-testid="portal-ba">
                  <h2 className="font-display text-base font-semibold text-[#0D2E24]">Avant / Après</h2>
                  <div className="mt-3 space-y-4">
                    {pairs.map((pr, i) => (
                      <div key={i} className="grid grid-cols-2 gap-2">
                        <figure><figcaption className="mb-1 text-xs font-semibold text-slate-500">Avant</figcaption>{imgs[pr.avant.id] && <img src={imgs[pr.avant.id]} alt="" onClick={() => lb.open([{ src: imgs[pr.avant.id], caption: "Avant" }, imgs[pr.apres.id] && { src: imgs[pr.apres.id], caption: "Après" }].filter(Boolean), 0)} className="h-40 w-full cursor-zoom-in rounded-lg object-cover" />}</figure>
                        <figure><figcaption className="mb-1 text-xs font-semibold text-emerald-700">Après</figcaption>{imgs[pr.apres.id] && <img src={imgs[pr.apres.id]} alt={pr.apres.caption || ""} onClick={() => lb.open([imgs[pr.avant.id] && { src: imgs[pr.avant.id], caption: "Avant" }, { src: imgs[pr.apres.id], caption: "Après" }].filter(Boolean), imgs[pr.avant.id] ? 1 : 0)} className="h-40 w-full cursor-zoom-in rounded-lg object-cover" />}</figure>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
              {singles.length > 0 && (
                <Card className="p-4">
                  <h2 className="font-display text-base font-semibold text-[#0D2E24]">Photos</h2>
                  <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {singles.map((p) => imgs[p.id] && <figure key={p.id}><img src={imgs[p.id]} alt={p.caption || ""} onClick={() => { const g = singles.filter((x) => imgs[x.id]).map((x) => ({ src: imgs[x.id], caption: x.caption })); lb.open(g, g.findIndex((x) => x.src === imgs[p.id])); }} className="h-32 w-full cursor-zoom-in rounded-lg object-cover" />{p.caption && <figcaption className="mt-1 text-xs text-slate-500">{p.caption}</figcaption>}</figure>)}
                  </div>
                </Card>
              )}
            </>
          );
        })()}

        {/* Documents */}
        {ws.documents.length > 0 && (
          <Card className="p-4">
            <h2 className="font-display text-base font-semibold text-[#0D2E24]">Documents</h2>
            <ul className="mt-3 space-y-2">
              {ws.documents.map((d) => <li key={d.id}><a href="#" onClick={(ev) => { ev.preventDefault(); downloadDoc(d.id, session, d.caption); }} className="flex items-center gap-2 text-sm text-emerald-700 hover:underline" data-testid={`portal-doc-${d.id}`}><Icons.FileText className="h-4 w-4" />{d.caption}</a></li>)}
            </ul>
          </Card>
        )}

        {/* Actions client */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Button onClick={() => { setReqKind("prestation"); setOpenReq(true); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="portal-request-btn"><Icons.PlusCircle className="mr-1.5 h-4 w-4" />Demander une prestation complémentaire</Button>
          <Button onClick={() => { setReqKind("probleme"); setOpenReq(true); }} variant="outline" data-testid="portal-report-btn"><Icons.TriangleAlert className="mr-1.5 h-4 w-4" />Signaler un problème</Button>
        </div>

        <p className="pt-2 text-center text-xs text-slate-400">SIGNA CLEAN · {ws.contact?.phone || ""} {ws.contact?.email || ""}</p>
      </div>

      {lb.node}
      <RequestDialog open={openReq} onOpenChange={setOpenReq} kind={reqKind} session={session} />
    </div>
  );
}

async function downloadDoc(fid, session, name) {
  try {
    const { data } = await axios.get(`${API}/track/portal/file/${fid}`, { headers: { Authorization: `Bearer ${session}` }, responseType: "blob" });
    const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = (name || "document") + ".pdf"; a.click(); URL.revokeObjectURL(url);
  } catch { toast.error("Document indisponible"); }
}

function Centered({ children }) {
  return <div className="flex min-h-screen flex-col items-center justify-center bg-[#F8FAFC] px-6 text-center">{children}</div>;
}

function RequestDialog({ open, onOpenChange, kind, session }) {
  const [f, setF] = useState({ description: "", contact_pref: "" });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => { if (open) { setF({ description: "", contact_pref: "" }); setDone(false); } }, [open]);
  const submit = async () => {
    setSaving(true);
    try { await axios.post(`${API}/track/portal/requests`, { kind, description: f.description, contact_pref: f.contact_pref }, { headers: { Authorization: `Bearer ${session}` } }); setDone(true); toast.success("Demande envoyée"); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec de l'envoi"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="portal-request-dialog">
        <DialogHeader><DialogTitle>{kind === "probleme" ? "Signaler un problème" : "Demander une prestation complémentaire"}</DialogTitle></DialogHeader>
        {done ? (
          <div className="py-6 text-center" data-testid="portal-request-done"><Icons.CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" /><p className="mt-2 font-semibold text-slate-700">Votre demande a bien été enregistrée.</p><p className="mt-1 text-sm text-slate-500">L'équipe SIGNA CLEAN vous recontactera. Une demande ne constitue pas une commande.</p></div>
        ) : (
          <>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Décrivez votre demande</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="portal-req-desc" /></div>
              <div className="space-y-1.5"><Label>Préférence de contact (facultatif)</Label><Input value={f.contact_pref} onChange={(e) => setF({ ...f, contact_pref: e.target.value })} placeholder="Téléphone / e-mail" data-testid="portal-req-contact" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button><Button onClick={submit} disabled={!f.description.trim() || saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="portal-req-submit">{saving ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : "Envoyer"}</Button></DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
